import {
  LoadedReportAction,
  ReportFormActions,
  ReportFormActionTypes,
  SetOptionsAction,
  SetPropertyAction,
  UpdatedReportAction
} from '../actions/reportFormActions';
import {
  ICreatedWebhookAction,
  IDeletedWebhookAction,
  IUpdatedWebhookAction,
  WebhookActionTypes
} from '../actions/webhookActions';
import {CreatedTagAction, RemovedTagAction, TagsActionTypes,} from '../actions/tagActions';
import {ApiStatus, Report,} from '../../models';
import {BaseBlock, fromBlocksToContent} from '../../components/blocks';

export const initialReportFormState: ReportFormState = {
  loadingStatus: null,
  updatingStatus: null,
  report: null, // current report
  blocks: null,
  isDirty: false,
  instant: false,
  optionsChanged: false,
  readOnly: true,
}

export interface ReportFormState {
  loadingStatus: ApiStatus | null;
  updatingStatus: ApiStatus | null;
  report: Report | null; // current report
  /*
   * `reportForm.blocks` are only used for reference so that we know which blocks to render.
   * These hold only initial information.
   * Any additional information (real, up to date) is saved to `block.blocks` reducer.
   */
  blocks: Partial<BaseBlock>[] | null, // blocks to render
  /*
   * Tells if the form `isDirty`, respectively `instant` tells to use debounce or update directly
   */
  isDirty: boolean;
  /*
   * Used to trigger useEffect when options are changed.
   */
  optionsChanged: boolean | number; // used to trigger useEffect when options are changed
  instant: boolean;
  /*
   * Based on user's permissions
   */
  readOnly: boolean;
}

export default function reportFormReducer(
  state: ReportFormState = initialReportFormState,
  action:
    ReportFormActions |
    ICreatedWebhookAction |
    IUpdatedWebhookAction |
    IDeletedWebhookAction |
    CreatedTagAction |
    RemovedTagAction
): ReportFormState {
  switch (action.type) {
    case ReportFormActionTypes.LOAD_REPORT:
      return {
        ...state,
        loadingStatus: ApiStatus.LOADING,
      }
    case ReportFormActionTypes.LOADING_REPORT_FAILED:
      return {
        ...state,
        loadingStatus: ApiStatus.FAILED,
      }
    case ReportFormActionTypes.LOADED_REPORT:
      return handleLoadedReport(state, action);

    case ReportFormActionTypes.UNSET_REPORT:
      return {
        ...state,
        report: null,
        loadingStatus: null,
        updatingStatus: null,
        blocks: null,
        isDirty: false,
        optionsChanged: false,
        instant: false,
        readOnly: true,
      };
    case ReportFormActionTypes.UPDATE_REPORT:
      return {
        ...state,
        updatingStatus: ApiStatus.LOADING,
      }
    case ReportFormActionTypes.UPDATING_REPORT_FAILED:
      return {
        ...state,
        updatingStatus: ApiStatus.FAILED,
      }
    case ReportFormActionTypes.UPDATED_REPORT:
      return handleUpdateReport(state, action);
    case ReportFormActionTypes.ADD_BLOCK:
      return handleAddBlock(state, action);
    case ReportFormActionTypes.ADD_BLOCKS:
      return handleAddBlocks(state, action);
    case ReportFormActionTypes.UPDATE_BLOCKS:
      return handleUpdateBlocks(state, action);
    case ReportFormActionTypes.UPDATE_BLOCKS_POSITION:
      return handleUpdatePosition(state, action);
    case ReportFormActionTypes.REMOVE_BLOCK:
      return handleRemoveBlock(state, action);
    case ReportFormActionTypes.SET_PROPERTY:
      return handleSetProperty(state, action);
    case ReportFormActionTypes.SET_OPTIONS:
      return handleSetOptions(state, action);
    case ReportFormActionTypes.SET_FOLDER:
      return handleUpdateReportFolder(state, action);
    case ReportFormActionTypes.SET_READ_ONLY:
      return {
        ...state,
        readOnly: action.payload,
      }
    /*
     * Tags
     */
    case TagsActionTypes.CREATED_TAG:
      return handleCreatedTag(state, action);
    case TagsActionTypes.REMOVED_TAG:
      return handleRemovedTag(state, action);
    /*
     * Webhook
     */
    case WebhookActionTypes.CREATED_WEBHOOK:
      return handleCreateUpdateWebhook(state, action);
    case WebhookActionTypes.UPDATED_WEBHOOK:
      return handleCreateUpdateWebhook(state, action);
    case WebhookActionTypes.DELETED_WEBHOOK:
      return {
        ...state,
        report: state.report ? {
          ...state.report,
          webhook: null
        } : null,
      }
    default:
      return state;
  }
}

// Functions

function _maxIndex(content: Partial<BaseBlock>[], type: string): number {
  /*
   * max index based on type
   */
  const regex = new RegExp(`${type}_(\\d+)`);

  const max = content.reduce((acc, c) => {
    if (c.type === type) {
      const match = c.name?.match(regex);

      if (!!match) {
        const index = parseInt(match[1]);
        acc = (acc < index) ? index : acc;
      }
    }
    return acc;
  }, 0);
  return max + 1;
}

function _findBlock(blocks: any[], id: string | number) {
  return blocks.find((b) => b.content_json.id === id);
}

function _setPosition(blocks: any[]) {
  /*
   * Set position based on block's position in the array
   */
  return blocks.map((block: any, index: number) => ({
    ...block,
    position: index,
  }));
}

function _insertNewBlocks(
  blocks: Partial<BaseBlock>[],
  newBlocks: Partial<BaseBlock>[],
  addAfter?: string
): Partial<BaseBlock>[] {
  /*
   * Insert *new* block(s) after given `uid`. It's always `after` and it only applies for *new* blocks.
   *
   * NOTE: position is calculated based on the index where a block is situated in the array, that's why we
   *   have to insert a block after a given `addAfter`, so that position is correct.
   *
   *   We don't update the position with this, but we tell where the new blocks should be (added after).
   */
  if (!addAfter) {
    /*
     * If `addAfter` does not exist, insert newBlocks at the end.
     */
    return [...blocks, ...newBlocks];
  }
  const found = blocks.find((b: Partial<BaseBlock>) => b.uid === addAfter);

  if (!found || (found && !found.hasOwnProperty('position'))) {
    /*
     * If `addAfter` block is not found, insert newBlocks at the end.
     */
    return [...blocks, ...newBlocks];
  }
  const foundPosition = found.position as number + 1; // insert after

  return [
    ...blocks.slice(0, foundPosition),
    ...newBlocks,
    ...blocks.slice(foundPosition),
  ]
}

function handleLoadedReport(state: ReportFormState, action: LoadedReportAction): ReportFormState {
  const {ordered_blocks} = action.payload;

  return {
    ...state,
    loadingStatus: ApiStatus.LOADED,
    report: action.payload, // we leave blocks as it is used for initial load in `block.blocks` reducer
    /*
     * On loaded report, maintain DB block position
     */
    blocks: _setPosition(fromBlocksToContent(ordered_blocks || [])),
  }
}

function handleUpdateReport(state: ReportFormState, action: UpdatedReportAction): ReportFormState {
  /*
   * This is called after API call
   */
  const report: any = state.report || {};

  return {
    ...state,
    report: {
      ...report,
      ...action.payload,
    },
    updatingStatus: ApiStatus.LOADED,
    isDirty: false,
    optionsChanged: false,
    instant: false,
  }
}

function handleAddBlock(state: ReportFormState, action: any): ReportFormState {
  const blocks: Partial<BaseBlock>[] = state.blocks || [];
  const {block, addAfter} = action.payload;

  const index = _maxIndex(blocks, block.type);
  const name = `${block.type}_${index}`;
  const newBlock: BaseBlock = {
    ...block,
    name,
    verbose_name: name,
  };

  return {
    ...state,
    blocks: _setPosition(_insertNewBlocks(blocks, [newBlock], addAfter)),
  }
}

function handleAddBlocks(state: ReportFormState, action: any): ReportFormState {
  /*
   * Here we don't set `isDirty`, that will be set by `update` when the actual blocks will have `uid` as well.
   */
  const blocks = state.blocks || [];

  const newBlocks = action.payload.blocks.map((block: any) => {
    let name = block.name; // name already exists for `condition_check` and `mail_merge` blocks

    if (!name) {
      const index = _maxIndex(blocks, block.type);
      name = `${block.type}_${index}`;
    }

    return {
      ...block,
      name,
      verbose_name: name,
    }
  });

  return {
    ...state,
    blocks: _setPosition(_insertNewBlocks(blocks, newBlocks, action.payload.addAfter)),
  }
}

function handleUpdateBlocks(state: ReportFormState, action: any): ReportFormState {
  /*
   * Update existing `state.blocks` with `uid`.
   */
  const reportBlocks = state.blocks || [];
  const { blocks } = action.payload;

  let isDirty = state.isDirty;

  const newBlocks = reportBlocks.map((c) => {
    if (!c.uid) {
      // If content doesn't have uid, get it from payload
      const newBlock = _findBlock(blocks, c.id as any);

      if (!newBlock) {
        return c;
      }
      isDirty = true; // blocks updated

      return {
        ...c,
        uid: newBlock.uid,
        block_id: newBlock.id,
      }
    }
    return c;
  });

  return {
    ...state,
    blocks: _setPosition(newBlocks?.length ? newBlocks : fromBlocksToContent(blocks)),
    isDirty: isDirty,
    instant: isDirty, // If new block is added, update instantly
  };
}

function handleUpdatePosition(state: ReportFormState, action: any): ReportFormState {
  const positionsMap = action.payload.reduce((acc: any, block_id: number, index: number) => {
    acc[block_id] = index;
    return acc;
  }, {});

  const _sortByPosition = (a: any, b: any) => {
    if (a.position > b.position) {
      return 1;
    } else if (a.position < b.position) {
      return -1;
    }
    return 0;
  }

  const newBlocks = state.blocks?.map((block: any) => ({
    ...block,
    position: positionsMap[block.block_id]
  }));

  return {
    ...state,
    blocks: newBlocks?.sort(_sortByPosition) || null,
    isDirty: true,
    instant: true,
  }
}

function handleRemoveBlock(state: ReportFormState, action: any): ReportFormState {
  /*
   * Try to find the block that's about to be removed.
   */
  const block = state.blocks?.find((b: any) => b.uid === action.payload);
  /*
   * Remove block by `uid`
   */
  let newBlocks = state.blocks?.filter((b: any) => b.uid !== action.payload) || null;

  if (block && block.hasOwnProperty('batchId')) {
    /*
     * If removed block is found, and it has `batchId` property, remove related blocks as well (IF / FOR statements)
     */
    newBlocks = state.blocks?.filter((b: any) => b.batchId !== block.batchId) || null;
  }

  return {
    ...state,
    blocks: newBlocks,
    isDirty: true,
  }
}

function handleSetProperty(state: ReportFormState, action: SetPropertyAction): ReportFormState {
  const report: any = state.report || {};

  return {
    ...state,
    report: report ? {
      ...report,
      [action.payload.key]: action.payload.value,
    } : null,
    isDirty: true,
  }
}

function handleSetOptions(state: ReportFormState, action: SetOptionsAction): ReportFormState {
  let newReport: any = state.report || {};

  newReport.options_json = {
    ...newReport.options_json,
    [action.payload.key]: action.payload.value,
  }; // mutate options_json

  let instant = state.instant;
  if (action.payload.key === 'destinations') {
    instant = true; // Hacky?!
  }

  let optionsChanged: number = state.optionsChanged === false ? 0 : state.optionsChanged as number;
  optionsChanged += 1; // increment options changed counter, in order to trigger `useEffect`

  return {
    ...state,
    report: newReport,
    isDirty: true,
    optionsChanged, // this is used to trigger `useEffect`
    instant,
  }
}

function handleUpdateReportFolder(
  state: ReportFormState,
  action: any,
): ReportFormState {
  const report = state.report;

  const newProps: any = {
    folder_id: action.payload?.id || null,
    folder: action.payload
  }

  return {
    ...state,
    updatingStatus: ApiStatus.LOADED,
    report: report ? {
      ...report,
      ...newProps,
    } : null,
  }
}

function handleCreatedTag(state: ReportFormState, action: CreatedTagAction): ReportFormState {
  const {tag, parent} = action.payload;
  const report = state.report;

  if (!parent || parent?.objectType !== 'report') {
    return state;
  } else if (!state?.report) {
    return state; // not on report edit page
  }

  return {
    ...state,
    report: report ? {
      ...state.report,
      tags: [
        tag,
        ...report.tags || [],
      ],
    } : null
  }
}

function handleRemovedTag(state: ReportFormState, action: RemovedTagAction): ReportFormState {
  const {uid, parent} = action.payload;
  const report = state.report;

  if (!parent || parent.objectType !== 'report') {
    return state;
  } else if (!state?.report) {
    return state; // not on report edit page
  }
  // tags: report.tags?.filter((t: any) => t.uid !== uid) || [],

  return {
    ...state,
    report: report ? {
      ...state.report,
      tags: report.tags?.filter((t: any) => t.uid !== uid) || [],
    } : null
  }
}

function handleCreateUpdateWebhook(
  state: ReportFormState,
  action: ICreatedWebhookAction | IUpdatedWebhookAction
): ReportFormState {
  const {webhook} = action.payload;

  const newWebhook = {
    ...(state.report?.webhook || {}),
    ...webhook,
  }

  if (webhook.recipients) {
    newWebhook.recipients_json = JSON.parse(webhook.recipients as string);
  }
  if (webhook.details) {
    newWebhook.details_json = JSON.parse(webhook.details as string);
  }

  return {
    ...state,
    report: state.report ? {
      ...state.report,
      webhook: newWebhook,
    } : null,
  }
}

// Selectors
